import type { ApiQuery, ApiRequestBody, ApiResponse } from "@takt/api-contracts"
import type { ApiClient } from "../client"
import type { PaginatedQuery } from "./query-types"

type ResourceId = string | number
const id = (value: ResourceId) => encodeURIComponent(String(value))
const companyPath = (companyId: ResourceId) => `/companies/${id(companyId)}`

export type CompanyEmployeesResponse = ApiResponse<"/v1/companies/{companyId}/employees", "get", 200>
export type AddCompanyEmployeeInput = ApiRequestBody<"/v1/companies/{companyId}/employees", "post">
export type UpdateCompanyEmployeeInput = ApiRequestBody<"/v1/companies/{companyId}/employees/{employeeId}", "patch">
export type UpsertCompanyEmployeeProfileInput = ApiRequestBody<
    "/v1/companies/{companyId}/employees/{employeeId}/profile",
    "put"
>
type GeneratedCompanyServicesQuery = PaginatedQuery<NonNullable<ApiQuery<"/v1/companies/{companyId}/services", "get">>>
export type CompanyServicesQuery = Omit<GeneratedCompanyServicesQuery, "isActive"> & { isActive?: boolean }
export type CompanyServicesResponse = ApiResponse<"/v1/companies/{companyId}/services", "get", 200>
export type CreateCompanyServiceInput = ApiRequestBody<"/v1/companies/{companyId}/services", "post">
export type UpdateCompanyServiceInput = ApiRequestBody<"/v1/companies/{companyId}/services/{serviceId}", "patch">
export type AssignEmployeeToServiceInput = ApiRequestBody<
    "/v1/companies/{companyId}/services/{serviceId}/employees",
    "post"
>
export type UpdateEmployeeServiceAssignmentInput = ApiRequestBody<
    "/v1/companies/{companyId}/services/{serviceId}/employees/{employeeId}",
    "patch"
>
export type ReplaceEmployeeWorkHoursInput = ApiRequestBody<
    "/v1/companies/{companyId}/employees/{employeeId}/work-hours",
    "put"
>
type GeneratedCompanyAvailabilityQuery = NonNullable<ApiQuery<"/v1/companies/{companyId}/availability", "get">>
export type CompanyAvailabilityQuery = Omit<GeneratedCompanyAvailabilityQuery, "serviceIds" | "assignAnyEmployee"> & {
    serviceIds: Array<string | number>
    assignAnyEmployee?: boolean
}
export type CompanyAvailabilityResponse = ApiResponse<"/v1/companies/{companyId}/availability", "get", 200>
export type CompanyAppointmentsQuery = PaginatedQuery<
    NonNullable<ApiQuery<"/v1/companies/{companyId}/appointments", "get">>
>
export type CompanyAppointmentsResponse = ApiResponse<"/v1/companies/{companyId}/appointments", "get", 200>
export type CompanyAppointmentResponse = ApiResponse<
    "/v1/companies/{companyId}/appointments/{appointmentId}",
    "get",
    200
>
export type CompanyAppointmentDecisionInput = ApiRequestBody<
    "/v1/companies/{companyId}/appointments/{appointmentId}/confirm",
    "post"
>
export type CompanyReviewsModerationQuery = PaginatedQuery<
    NonNullable<ApiQuery<"/v1/companies/{companyId}/reviews/moderation", "get">>
>
export type CompanyReviewsModerationResponse = ApiResponse<"/v1/companies/{companyId}/reviews/moderation", "get", 200>
export type HideCompanyReviewInput = ApiRequestBody<"/v1/companies/{companyId}/reviews/{reviewId}/hide", "post">
export type CompanyImagesResponse = ApiResponse<"/v1/companies/{companyId}/images", "get", 200>
export type CompanyImageManagementResponse = ApiResponse<"/v1/companies/{companyId}/images/management", "get", 200>
export type CreateCompanyImageUploadInput = ApiRequestBody<"/v1/companies/{companyId}/images/uploads", "post">
export type CreateCompanyImageUploadResponse = ApiResponse<"/v1/companies/{companyId}/images/uploads", "post", 201>
export type CompanyImageLifecycleResponse = ApiResponse<
    "/v1/companies/{companyId}/images/{imageId}/complete",
    "post",
    202
>

export function createMerchantEndpoints(client: ApiClient) {
    const appointmentAction = (
        companyId: ResourceId,
        appointmentId: ResourceId,
        action: "confirm" | "reject" | "cancel" | "complete" | "no-show",
        input: CompanyAppointmentDecisionInput = {},
    ) =>
        client.post<CompanyAppointmentResponse>(
            `${companyPath(companyId)}/appointments/${id(appointmentId)}/${action}`,
            input,
        )

    return {
        listCompanyImages(companyId: ResourceId) {
            return client.get<CompanyImagesResponse>(`${companyPath(companyId)}/images`)
        },
        getCompanyImageManagement(companyId: ResourceId, signal?: AbortSignal) {
            return client.get<CompanyImageManagementResponse>(`${companyPath(companyId)}/images/management`, {
                signal,
            })
        },
        createCompanyImageUpload(companyId: ResourceId, input: CreateCompanyImageUploadInput, signal?: AbortSignal) {
            return client.post<CreateCompanyImageUploadResponse>(`${companyPath(companyId)}/images/uploads`, input, {
                signal,
            })
        },
        completeCompanyImageUpload(companyId: ResourceId, imageId: ResourceId, signal?: AbortSignal) {
            return client.post<CompanyImageLifecycleResponse>(
                `${companyPath(companyId)}/images/${id(imageId)}/complete`,
                undefined,
                { signal },
            )
        },
        selectPrimaryCompanyImage(companyId: ResourceId, imageId: ResourceId) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/images/{imageId}/primary", "post", 200>>(
                `${companyPath(companyId)}/images/${id(imageId)}/primary`,
            )
        },
        deleteCompanyImage(companyId: ResourceId, imageId: ResourceId) {
            return client.delete<null>(`${companyPath(companyId)}/images/${id(imageId)}`)
        },
        listCompanyEmployees(companyId: ResourceId) {
            return client.get<CompanyEmployeesResponse>(`${companyPath(companyId)}/employees`)
        },
        addCompanyEmployee(companyId: ResourceId, input: AddCompanyEmployeeInput) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/employees", "post", 201>>(
                `${companyPath(companyId)}/employees`,
                input,
            )
        },
        updateCompanyEmployee(companyId: ResourceId, employeeId: string, input: UpdateCompanyEmployeeInput) {
            return client.patch<ApiResponse<"/v1/companies/{companyId}/employees/{employeeId}", "patch", 200>>(
                `${companyPath(companyId)}/employees/${id(employeeId)}`,
                input,
            )
        },
        removeCompanyEmployee(companyId: ResourceId, employeeId: string) {
            return client.delete<null>(`${companyPath(companyId)}/employees/${id(employeeId)}`)
        },
        upsertCompanyEmployeeProfile(
            companyId: ResourceId,
            employeeId: string,
            input: UpsertCompanyEmployeeProfileInput,
        ) {
            return client.put<ApiResponse<"/v1/companies/{companyId}/employees/{employeeId}/profile", "put", 200>>(
                `${companyPath(companyId)}/employees/${id(employeeId)}/profile`,
                input,
            )
        },
        listMerchantCompanyServices(companyId: ResourceId, query: CompanyServicesQuery = {}) {
            return client.get<CompanyServicesResponse>(`${companyPath(companyId)}/services`, { query })
        },
        getCompanyService(companyId: ResourceId, serviceId: ResourceId) {
            return client.get<ApiResponse<"/v1/companies/{companyId}/services/{serviceId}", "get", 200>>(
                `${companyPath(companyId)}/services/${id(serviceId)}`,
            )
        },
        createCompanyService(companyId: ResourceId, input: CreateCompanyServiceInput) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/services", "post", 201>>(
                `${companyPath(companyId)}/services`,
                input,
            )
        },
        updateCompanyService(companyId: ResourceId, serviceId: ResourceId, input: UpdateCompanyServiceInput) {
            return client.patch<ApiResponse<"/v1/companies/{companyId}/services/{serviceId}", "patch", 200>>(
                `${companyPath(companyId)}/services/${id(serviceId)}`,
                input,
            )
        },
        deleteCompanyService(companyId: ResourceId, serviceId: ResourceId) {
            return client.delete<null>(`${companyPath(companyId)}/services/${id(serviceId)}`)
        },
        listAssignedEmployeesForService(companyId: ResourceId, serviceId: ResourceId) {
            return client.get<ApiResponse<"/v1/companies/{companyId}/services/{serviceId}/employees", "get", 200>>(
                `${companyPath(companyId)}/services/${id(serviceId)}/employees`,
            )
        },
        assignEmployeeToService(companyId: ResourceId, serviceId: ResourceId, input: AssignEmployeeToServiceInput) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/services/{serviceId}/employees", "post", 201>>(
                `${companyPath(companyId)}/services/${id(serviceId)}/employees`,
                input,
            )
        },
        updateEmployeeServiceAssignment(
            companyId: ResourceId,
            serviceId: ResourceId,
            employeeId: string,
            input: UpdateEmployeeServiceAssignmentInput,
        ) {
            return client.patch<
                ApiResponse<"/v1/companies/{companyId}/services/{serviceId}/employees/{employeeId}", "patch", 200>
            >(`${companyPath(companyId)}/services/${id(serviceId)}/employees/${id(employeeId)}`, input)
        },
        removeEmployeeFromService(companyId: ResourceId, serviceId: ResourceId, employeeId: string) {
            return client.delete<null>(
                `${companyPath(companyId)}/services/${id(serviceId)}/employees/${id(employeeId)}`,
            )
        },
        listCompanyEmployeeWorkHours(companyId: ResourceId, employeeId: string) {
            return client.get<ApiResponse<"/v1/companies/{companyId}/employees/{employeeId}/work-hours", "get", 200>>(
                `${companyPath(companyId)}/employees/${id(employeeId)}/work-hours`,
            )
        },
        replaceCompanyEmployeeWorkHours(
            companyId: ResourceId,
            employeeId: string,
            input: ReplaceEmployeeWorkHoursInput,
        ) {
            return client.put<ApiResponse<"/v1/companies/{companyId}/employees/{employeeId}/work-hours", "put", 200>>(
                `${companyPath(companyId)}/employees/${id(employeeId)}/work-hours`,
                input,
            )
        },
        listCompanyAvailability(companyId: ResourceId, query: CompanyAvailabilityQuery) {
            return client.get<CompanyAvailabilityResponse>(`${companyPath(companyId)}/availability`, { query })
        },
        listCompanyAppointments(companyId: ResourceId, query: CompanyAppointmentsQuery = {}) {
            return client.get<CompanyAppointmentsResponse>(`${companyPath(companyId)}/appointments`, { query })
        },
        getCompanyAppointment(companyId: ResourceId, appointmentId: ResourceId) {
            return client.get<CompanyAppointmentResponse>(`${companyPath(companyId)}/appointments/${id(appointmentId)}`)
        },
        confirmCompanyAppointment: (
            companyId: ResourceId,
            appointmentId: ResourceId,
            input: CompanyAppointmentDecisionInput = {},
        ) => appointmentAction(companyId, appointmentId, "confirm", input),
        rejectCompanyAppointment: (
            companyId: ResourceId,
            appointmentId: ResourceId,
            input: CompanyAppointmentDecisionInput = {},
        ) => appointmentAction(companyId, appointmentId, "reject", input),
        cancelCompanyAppointment: (
            companyId: ResourceId,
            appointmentId: ResourceId,
            input: CompanyAppointmentDecisionInput = {},
        ) => appointmentAction(companyId, appointmentId, "cancel", input),
        completeCompanyAppointment: (
            companyId: ResourceId,
            appointmentId: ResourceId,
            input: CompanyAppointmentDecisionInput = {},
        ) => appointmentAction(companyId, appointmentId, "complete", input),
        markCompanyAppointmentAsNoShow: (
            companyId: ResourceId,
            appointmentId: ResourceId,
            input: CompanyAppointmentDecisionInput = {},
        ) => appointmentAction(companyId, appointmentId, "no-show", input),
        listCompanyReviewsForModeration(companyId: ResourceId, query: CompanyReviewsModerationQuery = {}) {
            return client.get<CompanyReviewsModerationResponse>(`${companyPath(companyId)}/reviews/moderation`, {
                query,
            })
        },
        hideCompanyReview(companyId: ResourceId, reviewId: ResourceId, input: HideCompanyReviewInput = {}) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/reviews/{reviewId}/hide", "post", 200>>(
                `${companyPath(companyId)}/reviews/${id(reviewId)}/hide`,
                input,
            )
        },
        restoreCompanyReview(companyId: ResourceId, reviewId: ResourceId) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/reviews/{reviewId}/restore", "post", 200>>(
                `${companyPath(companyId)}/reviews/${id(reviewId)}/restore`,
            )
        },
    }
}
